import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  User,
  Settings,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  AlertTriangle,
  Loader,
  Play,
  Package
} from 'lucide-react';
// Old SpinningSankalvaXLogo import - commented out
// import SpinningSankalvaXLogo from '../UI/SpinningSankalvaXLogo';
// New Gruve Logo imports
import GruveLogo from '../UI/GruveLogo';
import SpinningGruveLogo from '../UI/SpinningGruveLogo';
import PreSalesOrderCreationModal from './PreSalesOrderCreationModal';

/**
 * Sale Order V2 Chat Messages Component - NetSuite ONLY
 * For Salesforce, use SaleV2ChatMessagesSF in SaleOrderAgentSF folder
 */
const SaleV2ChatMessages = ({
  messages,
  isLoading,
  workflowName,
  error,
  runId,
  workflowId,
  token,
  sendMessage,
  executionStatus,
  currentStep,
  isReopenedSession = false,
  resumeTrigger = 0,
  draftSalesOrderData = null,
  setIsResuming = null,
  stopPolling = null,
  resumePolling = null
}) => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [showSalesOrderModal, setShowSalesOrderModal] = useState(null);
  const showSalesOrderModalRef = useRef(showSalesOrderModal);
  const [recentlyConfirmed, setRecentlyConfirmed] = useState(false);
  const processedMessageIdsRef = useRef(new Set());
  const hasInitializedRef = useRef(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [workflowContext, setWorkflowContext] = useState(null);

  // Fetch workflow context when modal is shown - NetSuite API
  useEffect(() => {
    if (showSalesOrderModal && runId && token) {
      const abortController = new AbortController();

      console.log('🔄 SaleV2ChatMessages: Fetching workflow context for runId:', runId);
      fetch(`/api/sale/workflows/status/${runId}`, {
        signal: abortController.signal,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data?.workflow_run?.run_context) {
            console.log('✅ Sale workflow context fetched:', data.data.workflow_run.run_context);
            setWorkflowContext(data.data.workflow_run.run_context);
          }
        })
        .catch(err => {
          if (err.name === 'AbortError') {
            console.log('🚫 Sale workflow context fetch aborted');
          } else {
            console.error('❌ Error fetching sale workflow context:', err);
          }
        });

      return () => {
        abortController.abort();
      };
    }
  }, [showSalesOrderModal, runId, token]);

  // Stop polling when Review modal is shown
  useEffect(() => {
    if (showSalesOrderModal && stopPolling) {
      console.log('⏸️ Stopping polling - Review modal shown');
      stopPolling();
    }
  }, [showSalesOrderModal, stopPolling]);

  console.log("📦 SaleV2ChatMessages mounted with props:", {
    messagesCount: messages?.length,
    isLoading,
    workflowName,
    error,
    runId,
    executionStatus,
    currentStep
  });

  // Update ref when state changes
  useEffect(() => {
    showSalesOrderModalRef.current = showSalesOrderModal;
  }, [showSalesOrderModal]);

  // Handle resume trigger - open PreSalesOrderCreationModal when Review button is clicked
  useEffect(() => {
    if (resumeTrigger > 0 && draftSalesOrderData) {
      console.log('🔄 Resume trigger activated, opening PreSalesOrderCreationModal with draft data:', draftSalesOrderData);

      // Wait a moment for visual feedback, then open modal
      setTimeout(() => {
        let dataToOpen = draftSalesOrderData;
        // Handle nested data structure if present
        if (draftSalesOrderData?.sales_order_data && !draftSalesOrderData?.line_items) {
          console.log('⚠️ Nested format detected in draft, extracting sales_order_data');
          dataToOpen = draftSalesOrderData.sales_order_data;
        }
        setShowSalesOrderModal({
          ...dataToOpen,
          runId: runId,
          workflowId: workflowId
        });
        if (setIsResuming) {
          setIsResuming(false);
        }
      }, 800); // 800ms to show the loading state
    }
  }, [resumeTrigger, draftSalesOrderData, runId, workflowId, setIsResuming]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Initialize processed messages on first mount - but DON'T add modal messages yet
  // so they can be processed for modal triggers first (fixes "Continue" from History)
  useEffect(() => {
    if (!hasInitializedRef.current && messages && messages.length > 0) {
      hasInitializedRef.current = true;
      messages.forEach(msg => {
        if (msg.id) {
          // Don't mark modal messages as processed during init - let modal logic handle them
          let hasModalComponent = false;
          try {
            let content = msg.content;
            if (typeof content === 'string') {
              const trimmed = content.trim();
              if (trimmed.startsWith('{')) {
                content = JSON.parse(trimmed);
              }
            }
            if (typeof content === 'object' && content?.component_definition?.metadata?.name === 'PreSalesOrderCreationModal') {
              hasModalComponent = true;
            }
          } catch (e) {}

          if (!hasModalComponent) {
            processedMessageIdsRef.current.add(msg.id);
          }
        }
      });
      console.log('📦 SaleV2ChatMessages: Initialized with', processedMessageIdsRef.current.size, 'processed messages (modal messages excluded)');
    }
  }, [messages]);

  // Process messages for Sales Order modal triggers
  useEffect(() => {
    if (!messages || messages.length === 0) return;

    console.log('📦 SaleV2ChatMessages: Processing', messages.length, 'messages for modal triggers');

    for (const message of messages) {
      // Skip if already processed
      if (message.id && processedMessageIdsRef.current.has(message.id)) {
        continue;
      }

      // Mark as processed
      if (message.id) {
        processedMessageIdsRef.current.add(message.id);
      }

      // Check if this is a UI component message with sales order data
      let parsedContent = null;
      try {
        if (typeof message.content === 'string') {
          const trimmed = message.content.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            parsedContent = JSON.parse(trimmed);
          }
        } else if (typeof message.content === 'object') {
          parsedContent = message.content;
        }
      } catch (e) {
        continue;
      }

      if (!parsedContent) continue;

      // Check for PreSalesOrderCreationModal component (NetSuite only)
      if (parsedContent.component_definition) {
        const componentDef = parsedContent.component_definition;
        console.log("📦 FOUND UI COMPONENT DEFINITION:", componentDef);

        // Check if it's the PreSalesOrderCreationModal (NetSuite only)
        if (componentDef.metadata?.name === 'PreSalesOrderCreationModal') {
          console.log("📦 FOUND NetSuite Sales Order Modal");

          // Don't show modal if workflow is already completed
          if (executionStatus === 'completed') {
            console.log("🚫 Sale workflow already completed - NOT showing modal");
            continue;
          }

          // Check if sales order was already created
          const salesOrderAlreadyCreated = messages.some(msg => {
            try {
              let content = msg.content;
              if (typeof content === 'string') {
                return content.includes('Sales order created successfully') ||
                       content.includes('sales_order_creation_successful') ||
                       content.includes('NetSuite Sales Order ID:');
              }
              if (typeof content === 'object' && content !== null) {
                return content.next_action_required === 'sales_order_creation_successful';
              }
            } catch (e) {
              return false;
            }
            return false;
          });

          if (salesOrderAlreadyCreated) {
            console.log("🚫 Sales order already created - not reopening modal");
            continue;
          }

          if (!showSalesOrderModal && !recentlyConfirmed) {
            // Extract sales order data
            const salesOrderData = parsedContent.sales_order_data || componentDef;
            console.log("📦 Opening PreSalesOrderCreationModal with data:", salesOrderData);
            setShowSalesOrderModal({
              ...salesOrderData,
              runId: runId,
              workflowId: workflowId
            });
            return;
          }
        }
      }

      // Check for sales order confirmation triggers
      // IMPORTANT: Only trigger modal from actual UI step, NOT from intermediate AI steps
      // The "proceed_to_review" action from AI steps just means "continue to next step"
      // We should NOT open the modal based on these - only from PreSalesOrderCreationModal component
      const salesOrderTriggers = [
        "confirm_sales_order_creation",
        "proceed_to_create_sales_order",
        "request_user_confirmation"
        // REMOVED: "proceed_to_review" - this triggers too early from AI steps
      ];

      if (salesOrderTriggers.includes(parsedContent.next_action) ||
          salesOrderTriggers.includes(parsedContent.next_action_required)) {
        console.log("📦 FOUND SALES ORDER TRIGGER:", parsedContent.next_action || parsedContent.next_action_required);

        // Only open if this message has sales_order_data with actual line items
        // This prevents opening from intermediate AI step outputs
        if (!showSalesOrderModal && !recentlyConfirmed && parsedContent.sales_order_data?.line_items) {
          const salesOrderData = parsedContent.sales_order_data || parsedContent;
          console.log("📦 Opening sales order modal with data:", salesOrderData);
          setShowSalesOrderModal({
            ...salesOrderData,
            runId: runId,
            workflowId: workflowId
          });
          return;
        }
      }
    }
  }, [messages, executionStatus, runId, workflowId, showSalesOrderModal, recentlyConfirmed]);

  // Message type icons
  const getMessageIcon = (type) => {
    switch (type) {
      case 'user':
        return <User className="w-4 h-4" />;
      case 'assistant':
        return <Bot className="w-4 h-4" />;
      case 'system':
        return <Settings className="w-4 h-4" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4" />;
      case 'tool_call':
        return <Play className="w-4 h-4" />;
      case 'tool_result':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Package className="w-4 h-4" />;
    }
  };

  const getMessageStyles = (type) => {
    switch (type) {
      case 'user':
        return {
          container: 'justify-end',
          bubble: 'bg-emerald-600 text-white',
          icon: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400'
        };
      case 'assistant':
        return {
          container: 'justify-start',
          bubble: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700',
          icon: 'bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400'
        };
      case 'system':
        return {
          container: 'justify-center',
          bubble: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700',
          icon: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        };
      case 'error':
        return {
          container: 'justify-start',
          bubble: 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800',
          icon: 'bg-red-100 text-red-600 dark:text-red-900/20 dark:text-red-400'
        };
      case 'tool_call':
        return {
          container: 'justify-start',
          bubble: 'bg-orange-50 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-800',
          icon: 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400'
        };
      case 'tool_result':
        return {
          container: 'justify-start',
          bubble: 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-800',
          icon: 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
        };
      default:
        return {
          container: 'justify-start',
          bubble: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
          icon: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render tool messages - same style as bill processing
  const renderToolMessage = (content, type) => {
    // Handle string content with line parsing
    if (typeof content === 'string') {
      const lines = content.split('\n');
      const parts = [];
      let isJsonSection = false;
      let jsonContent = '';

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];

        // Check if this line starts a JSON section (Parameters: or Result:)
        if (line.startsWith('Parameters:') || line.startsWith('Result:')) {
          // Extract the label part
          const colonIndex = line.indexOf(':');
          const label = line.substring(0, colonIndex + 1);
          const remainder = line.substring(colonIndex + 1).trim();

          // Add the label
          parts.push(
            <div key={`label-${i}`} className="text-sm font-medium text-gray-600 dark:text-gray-400 mt-2 mb-1">
              {label}
            </div>
          );

          isJsonSection = true;
          // If there's content after the colon (like "Parameters: {"), start collecting it
          jsonContent = remainder || '';
          continue;
        }

        // If we're in a JSON section, collect the content
        if (isJsonSection) {
          if (line.trim() === '' && jsonContent === '') {
            continue; // Skip empty lines at the start of JSON
          }

          if (line.startsWith('Executing:') || line.startsWith('Completed:')) {
            // We've hit the next section, render the collected JSON
            if (jsonContent.trim()) {
              let netsuiteUrl = null;
              try {
                const parsedJson = JSON.parse(jsonContent.trim());
                netsuiteUrl = parsedJson.netsuite_url || parsedJson.sales_order_url || parsedJson.netsuite_sales_order_url;
              } catch (e) {
                // If parsing fails, just show as text
              }

              parts.push(
                <div key={`json-${i}`} className="mt-1">
                  <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 font-mono text-xs overflow-x-auto max-w-full">
                    <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-all overflow-wrap-anywhere" style={{ wordBreak: 'break-word' }}>{jsonContent.trim()}</pre>
                  </div>
                  {netsuiteUrl && (
                    <div className="mt-2">
                      <a
                        href={netsuiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-md shadow-sm transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        View Sales Order in NetSuite
                      </a>
                    </div>
                  )}
                </div>
              );
            }
            isJsonSection = false;
            jsonContent = '';

            // Process the current line as a header
            parts.push(
              <div key={i} className="font-medium text-gray-900 dark:text-white">
                {line}
              </div>
            );
          } else {
            jsonContent += (jsonContent ? '\n' : '') + line;
          }
        } else if (line.startsWith('Executing:') || line.startsWith('Completed:')) {
          parts.push(
            <div key={i} className="font-medium text-gray-900 dark:text-white">
              {line}
            </div>
          );
        } else if (line.trim()) {
          parts.push(
            <div key={i} className="text-gray-700 dark:text-gray-300">
              {line}
            </div>
          );
        }
      }

      // Handle any remaining JSON content at the end
      if (isJsonSection && jsonContent.trim()) {
        let netsuiteUrl = null;
        try {
          const parsedJson = JSON.parse(jsonContent.trim());
          netsuiteUrl = parsedJson.netsuite_url || parsedJson.sales_order_url || parsedJson.netsuite_sales_order_url;
        } catch (e) {
          // If parsing fails, just show as text
        }

        parts.push(
          <div key="final-json" className="mt-1">
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 font-mono text-sm overflow-x-auto">
              <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">{jsonContent.trim()}</pre>
            </div>
            {netsuiteUrl && (
              <div className="mt-2">
                <a
                  href={netsuiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-md shadow-sm transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  View Sales Order in NetSuite
                </a>
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="space-y-1">
          {parts}
        </div>
      );
    }

    // Fallback for non-string content
    return (
      <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3 font-mono text-sm overflow-x-auto">
        <pre className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
          {typeof content === 'object' ? JSON.stringify(content, null, 2) : String(content)}
        </pre>
      </div>
    );
  };

  const renderMessageContent = (content, type, message) => {
    if (type === 'tool_call' || type === 'tool_result') {
      return renderToolMessage(content, type);
    }

    // UI component messages
    if (message && message.isUIComponent && typeof content === 'object' && content.component_definition) {
      return (
        <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4">
          <div className="text-teal-800 dark:text-teal-200 font-medium">
            {content.message || 'Sales Order review ready'}
          </div>
        </div>
      );
    }

    // Try to parse string content that might be JSON (including markdown code blocks)
    let parsedContent = content;
    if (typeof content === 'string') {
      // Remove markdown code block wrapper if present
      let jsonStr = content.trim();
      if (jsonStr.startsWith('```json')) {
        jsonStr = jsonStr.replace(/^```json\s*/, '').replace(/```\s*$/, '').trim();
      } else if (jsonStr.startsWith('```')) {
        jsonStr = jsonStr.replace(/^```\s*/, '').replace(/```\s*$/, '').trim();
      }

      // Try to parse as JSON
      if (jsonStr.startsWith('{') || jsonStr.startsWith('[')) {
        try {
          parsedContent = JSON.parse(jsonStr);
        } catch (e) {
          // Not valid JSON, keep original content
        }
      }
    }

    // Check for sales order creation success - show nice success message
    if (typeof parsedContent === 'object' && parsedContent !== null) {
      if (parsedContent.next_action === 'sales_order_created' && parsedContent.success === true) {
        const salesOrderId = parsedContent.sales_order_id || parsedContent.salesOrderId;
        const netsuiteUrl = parsedContent.netsuite_url ||
          `https://11315770-sb1.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${salesOrderId}`;

        return (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-green-800 dark:text-green-200">
                  Sales Order Created Successfully!
                </h4>
                <p className="text-green-700 dark:text-green-300 text-sm">
                  Sales Order ID: <span className="font-mono font-bold">{salesOrderId}</span>
                </p>
              </div>
            </div>
            {parsedContent.message && (
              <p className="text-green-700 dark:text-green-300 mb-3">{parsedContent.message}</p>
            )}
            <a
              href={netsuiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md shadow-sm transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              View in NetSuite
            </a>
          </div>
        );
      }

      // Check for creation failed
      if (parsedContent.next_action === 'creation_failed' || (parsedContent.success === false && parsedContent.error)) {
        return (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 dark:bg-red-800 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-red-800 dark:text-red-200">
                  Sales Order Creation Failed
                </h4>
                <p className="text-red-700 dark:text-red-300 text-sm">
                  {parsedContent.message || parsedContent.error || 'An error occurred'}
                </p>
              </div>
            </div>
          </div>
        );
      }

      // PRIORITY: Always show message_for_user when present (e.g., clarification requests)
      if (parsedContent.message_for_user) {
        return (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <span className="text-yellow-600">⚠️</span>
              <div className="whitespace-pre-wrap break-words text-yellow-800 dark:text-yellow-200">
                {parsedContent.message_for_user}
              </div>
            </div>
          </div>
        );
      }

      // Hide ALL intermediate step messages - these are internal workflow steps
      // Only show sales_order_created (success) or creation_failed (error)
      const shownActions = ['sales_order_created', 'creation_failed', 'request_user_clarification'];

      if (parsedContent.next_action && !shownActions.includes(parsedContent.next_action)) {
        // Hide all intermediate workflow step messages
        return null;
      }

      // Also hide any success message without sales_order_id
      if (parsedContent.success === true && !parsedContent.sales_order_id && !parsedContent.error) {
        return null;
      }

      if (parsedContent.message_for_user) {
        return <div className="whitespace-pre-wrap break-words">{parsedContent.message_for_user}</div>;
      }
      if (parsedContent.message) {
        return <div className="whitespace-pre-wrap break-words">{parsedContent.message}</div>;
      }

      // For component_definition messages, show a simple status
      if (parsedContent.component_definition || parsedContent.status === 'ui_ready') {
        return (
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
            <p className="text-orange-700 dark:text-orange-300">
              📋 {parsedContent.message || 'Sales order review ready'}
            </p>
          </div>
        );
      }

      // Fallback - but try to show something useful
      return (
        <div className="whitespace-pre-wrap break-words text-sm text-gray-600 dark:text-gray-400">
          {parsedContent.message || parsedContent.status || JSON.stringify(parsedContent, null, 2)}
        </div>
      );
    }

    // String content
    if (typeof content === 'string') {
      return (
        <div className="whitespace-pre-wrap break-words">
          {content}
        </div>
      );
    }

    return null;
  };

  // Sales order confirmation modal handlers
  const handleConfirmSalesOrder = async (finalSalesOrderData) => {
    try {
      console.log('📦 SaleV2ChatMessages: handleConfirmSalesOrder called with:', finalSalesOrderData);

      // Close modal immediately
      setShowSalesOrderModal(null);
      setRecentlyConfirmed(true);
      setIsProcessing(true);

      // Resume polling before sending message
      if (resumePolling) {
        console.log('▶️ Resuming polling - Create Sales Order clicked');
        resumePolling();
      }

      // Prepare sales order data for API - use the hook's sendMessage function
      // This ensures proper polling state management
      const userMessage = `Sales Order confirmed for creation with the following data: ${JSON.stringify({
        next_action_required: 'create_sales_order',
        sale_data: finalSalesOrderData
      })}`;

      console.log('📦 Using sendMessage hook to continue workflow with sales order creation');

      // Use the parent's sendMessage function from useSaleWorkflow hook
      // This properly manages polling state and timestamp tracking
      if (sendMessage) {
        await sendMessage(userMessage);
        console.log('✅ Sales order creation message sent via hook');
      } else {
        console.error('❌ sendMessage function not available');
      }

      setIsProcessing(false);

    } catch (err) {
      console.error('❌ Error confirming sales order:', err);
      setIsProcessing(false);
      setShowSalesOrderModal(null);
    }
  };

  const handleCancelSalesOrder = () => {
    console.log('📦 Sales order creation cancelled by user');
    setShowSalesOrderModal(null);
  };

  const handleCloseSalesOrder = () => {
    console.log('📦 Sales order modal closed');
    setShowSalesOrderModal(null);
  };

  const handleModifySalesOrder = (modifiedData) => {
    console.log('📦 Sales order modified:', modifiedData);
    setShowSalesOrderModal(modifiedData);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {/* Error message */}
      {error && (
        <div className="flex justify-center">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-red-700 dark:text-red-200 max-w-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      {messages && messages.map((message, index) => {
        const styles = getMessageStyles(message.type);

        // Skip rendering UI component messages as chat bubbles
        if (message.isUIComponent) {
          return null;
        }

        // Tool messages need more width for JSON display
        const isToolMessage = message.type === 'tool_call' || message.type === 'tool_result';
        const maxWidthClass = isToolMessage
          ? 'max-w-[95%] lg:max-w-[90%]'  // Wider for tool messages
          : 'max-w-[90%] lg:max-w-[80%]';  // Normal for other messages

        return (
          <div key={message.id || index} className={`flex ${styles.container}`}>
            <div className={`flex items-start gap-3 ${maxWidthClass} min-w-0`}>
              {message.type !== 'user' && (
                <div className={`p-2 rounded-full flex-shrink-0 ${styles.icon}`}>
                  {getMessageIcon(message.type)}
                </div>
              )}
              <div className={`rounded-lg px-4 py-3 shadow-sm min-w-0 flex-1 ${styles.bubble}`}>
                {renderMessageContent(message.content, message.type, message)}
                {message.timestamp && (
                  <div className="text-xs opacity-60 mt-2">
                    {formatTimestamp(message.timestamp)}
                  </div>
                )}
              </div>
              {message.type === 'user' && (
                <div className={`p-2 rounded-full ${styles.icon}`}>
                  {getMessageIcon(message.type)}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Loading indicator */}
      {(isLoading || isProcessing) && (
        <div className="flex justify-start">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-full bg-teal-100 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400">
              <Package className="w-4 h-4" />
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-center">
                {/* Old: <SpinningSankalvaXLogo size="w-5 h-5" className="text-teal-600 dark:text-teal-400" showBackground={true} /> */}
                <SpinningGruveLogo size="w-5 h-5" className="text-teal-600 dark:text-teal-400" showBackground={true} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scroll anchor */}
      <div ref={messagesEndRef} />

      {/* NetSuite Sales Order Confirmation Modal */}
      {showSalesOrderModal && (
        <PreSalesOrderCreationModal
          salesOrderData={showSalesOrderModal}
          onConfirm={handleConfirmSalesOrder}
          onCancel={handleCancelSalesOrder}
          onClose={handleCloseSalesOrder}
          onModify={handleModifySalesOrder}
          token={token}
          workflowContext={workflowContext || { workflowId, runId }}
          runId={runId}
          workflowId={workflowId}
        />
      )}

      {console.log('📦 SaleV2ChatMessages: Modal render check - showSalesOrderModal:', showSalesOrderModal)}
    </div>
  );
};

export default SaleV2ChatMessages;
